<template>
    <div class="block-editor-page">
        <div class="page-header">
            <div class="header-left">
                <a-button @click="goBack" icon="arrow-left">
                    {{ $t("message.back") || "Back" }}
                </a-button>
                <h1>
                    {{
                        isEditMode
                            ? $t("chatbotv2.editBlock") || "Edit Block"
                            : $t("chatbotv2.createBlock") || "Create Block"
                    }}
                </h1>
            </div>
            <div class="header-actions">
                <a-button 
                    v-if="!isPublishedView"
                    @click="saveDraft" 
                    :loading="saving" 
                    type="default"
                >
                    {{ $t("chatbotv2.saveDraft") || "Save Draft" }}
                </a-button>
                <a-button
                    v-if="isEditMode && block && _.get(block, 'draftVersion', false) && !isPublishedView"
                    type="primary"
                    @click="publishNow"
                    :loading="publishing"
                >
                    {{ $t("chatbotv2.publish") || "Publish" }}
                </a-button>
                <a-tag v-if="isPublishedView" color="green" style="margin: 0; padding: 8px 16px; font-size: 14px;">
                    <a-icon type="lock" /> {{ $t("chatbotv2.readOnlyMode") }}
                </a-tag>
            </div>
        </div>

        <div class="editor-content">
            <!-- Version Tabs (only in edit mode) -->
            <div v-if="isEditMode && block" class="version-tabs-wrapper">
                <a-tabs v-model="activeVersionTab" type="card" @change="onVersionTabChange">
                    <a-tab-pane key="draft">
                        <span slot="tab" style="display: flex; align-items: center; gap: 4px;">
                            <i class="material-icons" style="font-size: 16px;">edit</i>
                            {{ $t("chatbotv2.draft") }}
                        </span>
                    </a-tab-pane>
                    <a-tab-pane key="published" :disabled="!block.publishedVersion">
                        <span slot="tab" style="display: flex; align-items: center; gap: 4px;">
                            <i class="material-icons" style="font-size: 16px;">check_circle</i>
                            {{ $t("chatbotv2.published") }}
                            <span v-if="!block.publishedVersion" style="color: #999; font-size: 12px;">({{ $t("chatbotv2.notPublished") }})</span>
                        </span>
                    </a-tab-pane>
                </a-tabs>
            </div>

            <a-form :form="form" :label-col="{ span: 24 }" :wrapper-col="{ span: 24 }">
                <!-- Basic Info -->
                <div class="section">
                    <h3>{{ $t("chatbotv2.basicInfo") || "Basic Info" }}</h3>

                    <a-form-item :label="$t('chatbotv2.blockName') || 'Block Name'">
                        <a-input
                            v-model="formData.name"
                            size="large"
                            :disabled="isPublishedView"
                            :placeholder="$t('chatbotv2.enterBlockName') || 'Enter block name'"
                        />
                    </a-form-item>

                    <a-form-item :label="$t('chatbotv2.description') || 'Description'">
                        <a-textarea
                            v-model="formData.description"
                            :rows="3"
                            size="large"
                            :disabled="isPublishedView"
                            :placeholder="$t('chatbotv2.enterDescription') || 'Enter description'"
                        />
                    </a-form-item>

                    <a-row :gutter="16">
                        <a-col :span="8">
                            <a-form-item :label="$t('chatbotv2.icon') || 'Icon'">
                                <MaterialIconPicker v-model="formData.icon" :disabled="isPublishedView" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="8">
                            <a-form-item :label="$t('chatbotv2.category') || 'Category'">
                                <a-auto-complete
                                    v-model="formData.category"
                                    size="large"
                                    :disabled="isPublishedView"
                                    :data-source="filteredCategoryLabels"
                                    :placeholder="$t('chatbotv2.selectOrTypeCategory') || 'Select or type category'"
                                    :filter-option="(input, option) => option.toUpperCase().indexOf(input.toUpperCase()) !== -1"
                                >
                                    <a-input size="large" allowClear>
                                        <a-icon slot="prefix" type="tag" style="color: rgba(0,0,0,.25)" />
                                    </a-input>
                                </a-auto-complete>
                            </a-form-item>
                        </a-col>
                        <a-col :span="8">
                            <a-form-item :label="$t('chatbotv2.tags') || 'Tags'">
                                <a-select
                                    v-model="formData.tags"
                                    mode="tags"
                                    size="large"
                                    :disabled="isPublishedView"
                                    :placeholder="$t('chatbotv2.addTags') || 'Add tags'"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>
                </div>

                <!-- Code Editor -->
                <div class="section">
                    <h3>{{ $t("chatbotv2.javascriptCode") || "JavaScript Code" }}</h3>
                    <div class="code-editor-wrapper">
                        <div class="code-editor-header">
                            <div class="tips">
                                <div class="tip">
                                    <md-icon>info</md-icon>
                                    <span>{{ $t("chatbotv2.blockJsDesc") }}</span>
                                </div>
                                <div class="tip">
                                    <md-icon>warning</md-icon>
                                    <span
                                        >{{ $t("chatbotv2.jsSecurityNotice") }}</span
                                    >
                                </div>
                                <div v-if="isPublishedView" class="tip" style="color: #52c41a;">
                                    <md-icon>lock</md-icon>
                                    <span><strong>{{ $t("chatbotv2.publishedVersionReadOnly") }}</strong></span>
                                </div>
                            </div>
                            <div class="actions">
                                <a-button type="primary" @click="toggleFullScreen" style="margin-right: 10px">
                                    <i class="material-icons" style="font-size: 15px; margin-right: 5px">
                                        {{ isFullScreen ? "fullscreen_exit" : "fullscreen" }}
                                    </i>
                                    {{ isFullScreen ? $t("chatbotv2.exitFullScreen") : $t("chatbotv2.fullScreen") }}
                                </a-button>
                                <a-button 
                                    v-if="!isPublishedView"
                                    type="primary" 
                                    class="ai-assistant-btn" 
                                    @click="showPromptWizard"
                                >
                                    <img src="/static/img/copilot.png" width="15" height="15" style="margin-right: 5px" />
                                    {{ $t("chatbotv2.supsisAssistant") }}
                                </a-button>
                            </div>
                        </div>

                        <div class="code-editor">
                            <div class="available-libraries">
                                <span>{{ $t("chatbotv2.availableLibraries") }}</span>
                                <code>{{ $t("auto.blockEditorLibMoment") }}</code>, <code>{{ $t("auto.blockEditorLibLodash") }}</code>, <code>{{ $t("auto.blockEditorLibAxios") }}</code>,
                                <code>{{ $t("auto.blockEditorLibOpenAI") }}</code>, <code>{{ $t("auto.blockEditorLibJSDOM") }}</code>, <code>{{ $t("auto.blockEditorLibValidator") }}</code>
                            </div>

                            <div class="available-variables">
                                <span>{{ $t("chatbotv2.availableVariables") }}</span>
                                <code>{{ $t("chatbotv2.blockCodeReadAttribute") }}</code>, <code>{{ $t("chatbotv2.blockCodeSendMessage") }}</code>,
                                <code>{{ $t("chatbotv2.blockCodeSetAttributes") }}</code>, <code>{{ $t("chatbotv2.blockCodeSupsisUsers") }}</code>,
                                <code>{{ $t("chatbotv2.blockCodeSupsisContact") }}</code>{{ $t("auto.blockEditorVariablesTail") }}
                            </div>

                            <div class="attribute-help">
                                <span>{{ $t("chatbotv2.parameters") }}:</span>
                                {{ $t("chatbotv2.parametersAutoAvailable") }}
                            </div>

                            <!-- Parameter Usage Info -->
                            <div class="parameter-usage-info" v-if="formData.parameters.length > 0">
                                <div class="info-header">
                                    <a-icon type="info-circle" />
                                    <span>{{ $t("chatbotv2.definedParametersUsage") }}</span>
                                </div>
                                <div class="param-list">
                                    <div class="param-usage-item" v-for="param in formData.parameters" :key="param.name">
                                        <code class="param-var">{{ param.name }}</code>
                                        <span class="param-desc">{{ param.label }} ({{ param.type }})</span>
                                    </div>
                                </div>
                                <div class="usage-examples">
                                    <div class="example-item">
                                        <strong>{{ $t("chatbotv2.directUsage") }}</strong>
                                        <code>{{ $t("chatbotv2.blockEditorExampleLinePrefix") }}{{ $_.get(formData, 'parameters[0].name', $t('chatbotv2.exampleParameterFallback')) }}{{ $t("chatbotv2.blockEditorExampleLineSuffix") }}</code>
                                    </div>
                                    <div class="example-item">
                                        <strong>{{ $t("chatbotv2.readAllParameters") }}</strong>
                                        <code>{{ $t("chatbotv2.blockCodeReadAllExample") }}</code>
                                    </div>
                                </div>
                            </div>

                            <!-- Parameter Injection Notice -->
                            <div class="param-injection-notice">
                                <a-icon type="bulb" theme="twoTone" two-tone-color="#faad14" />
                                <div class="notice-content">
                                    <strong>{{ $t("chatbotv2.parametersAutoInjected") }}</strong>
                                    <p>{{ $t("chatbotv2.parametersAutoInjectedDesc") }}</p>
                                </div>
                            </div>

                            <div
                                id="custom-block-editor-monaco"
                                class="monaco-editor-container"
                                :class="{ 'fullscreen-editor': isFullScreen }"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Parameters -->
                <div class="section">
                    <h3>
                        {{ $t("chatbotv2.parameters") || "Parameters" }}
                        <div class="param-actions" v-if="!isPublishedView">
                            <a-button type="link" @click="addParameter" icon="plus">
                                {{ $t("chatbotv2.addParameter") }}
                            </a-button>
                            <a-button type="link" @click="showImportJsonSchema" icon="import">
                                {{ $t("chatbotv2.importJsonSchema") }}
                            </a-button>
                        </div>
                    </h3>

                    <div class="parameters-builder">
                        <div class="parameter-card" v-for="(param, index) in formData.parameters" :key="index">
                            <div class="param-header">
                                <span class="param-number">#{{ index + 1 }}</span>
                                <a-button 
                                    v-if="!isPublishedView"
                                    type="danger" 
                                    size="small" 
                                    @click="removeParameter(index)" 
                                    icon="delete"
                                >
                                    {{ $t("message.delete") || "Delete" }}
                                </a-button>
                            </div>

                            <div class="param-row">
                                <a-input
                                    v-model="param.name"
                                    :disabled="isPublishedView"
                                    :placeholder="$t('chatbotv2.parameterName') || 'Parameter name (variable)'"
                                    style="width: 180px"
                                />
                                <a-input
                                    v-model="param.label"
                                    :disabled="isPublishedView"
                                    :placeholder="$t('chatbotv2.parameterLabel') || 'Display label'"
                                    style="flex: 1"
                                />
                                <a-select v-model="param.type" :disabled="isPublishedView" style="width: 150px">
                                    <a-select-option value="text">{{ $t("auto.paramTypeText") }}</a-select-option>
                                    <a-select-option value="number">{{ $t("auto.paramTypeNumber") }}</a-select-option>
                                    <a-select-option value="boolean">{{ $t("auto.paramTypeBoolean") }}</a-select-option>
                                    <a-select-option value="json">{{ $t("auto.paramTypeJson") }}</a-select-option>
                                    <a-select-option value="json-structured">{{ $t("auto.paramTypeJsonStructured") }}</a-select-option>
                                    <a-select-option value="array">{{ $t("auto.paramTypeArray") }}</a-select-option>
                                    <a-select-option value="userid">{{ $t("auto.paramTypeUserId") }}</a-select-option>
                                    <a-select-option value="userid_list">{{ $t("auto.paramTypeUserIdList") }}</a-select-option>
                                    <a-select-option value="chatid">{{ $t("auto.paramTypeChatId") }}</a-select-option>
                                    <a-select-option value="contactid">{{ $t("auto.paramTypeContactId") }}</a-select-option>
                                </a-select>
                                <a-select v-model="param.inputMode" :disabled="isPublishedView" style="width: 120px">
                                    <a-select-option value="static">{{ $t("auto.chatbotStatic") }}</a-select-option>
                                    <a-select-option value="dynamic">{{ $t("auto.chatbotDynamic") }}</a-select-option>
                                </a-select>
                            </div>

                            <div class="param-row" v-if="param.type === 'json-structured'" style="background: #f0faff; padding: 10px; border: 1px solid #bae7ff; border-radius: 4px;">
                                <div class="structured-config" style="width: 100%;">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span style="font-weight: 600; color: #0050b3; display: flex; align-items: center; gap: 5px;">
                                            <a-icon type="cluster" /> {{ $t("auto.blockEditorStructuredJsonSchemaHeading") }}
                                        </span>
                                        <div style="display: flex; gap: 8px;" v-if="!isPublishedView">
                                            <a-button type="primary" size="small" @click="configureSchema(index)">
                                                {{ (param.schema && param.schema.length) ? $t('auto.blockEditorRebuildFromJson') : $t('chatbotv2.defineSchema') }}
                                            </a-button>
                                            <a-button 
                                                v-if="param.schema && param.schema.length" 
                                                type="default" 
                                                size="small" 
                                                @click="editSchemaDetails(index)"
                                            >
                                                <a-icon type="setting" /> {{ $t('chatbotv2.detailedSettings') }}
                                            </a-button>
                                        </div>
                                    </div>
                                    <div v-if="param.schema && param.schema.length" class="schema-preview">
                                        <div style="font-size: 12px; color: #666">
                                            <strong>{{ $t("auto.blockEditorFieldsLabel") }}</strong> {{ param.schema.map(f => f.name).join(', ') }}
                                            <span v-if="param.isList" class="ml-2">
                                                <a-tag color="blue">{{ $t("auto.blockEditorListArrayTag") }}</a-tag>
                                            </span>
                                        </div>
                                    </div>
                                    <div v-else style="font-size: 12px; color: #999; font-style: italic;">
                                        {{ $t("chatbotv2.noStructureDefined") }}
                                    </div>
                                </div>
                            </div>

                            <div class="param-row">
                                <a-input
                                    v-model="param.defaultValue"
                                    :disabled="isPublishedView"
                                    :placeholder="$t('chatbotv2.defaultValue')"
                                    style="flex: 1"
                                >
                                    <a-button 
                                        v-if="!isPublishedView"
                                        slot="addonAfter" 
                                        @click="selectSystemAttribute(index)" 
                                        size="small"
                                    >
                                        <a-icon type="database" />
                                        {{ $t("auto.blockEditorSystemAttr") }}
                                    </a-button>
                                </a-input>
                                <a-checkbox v-model="param.required" :disabled="isPublishedView">{{ $t("chatbotv2.isRequired") }}</a-checkbox>
                                <a-checkbox v-model="param.hidden" :disabled="isPublishedView">{{ $t("auto.blockEditorHidden") }}</a-checkbox>
                            </div>

                            <div class="param-row">
                                <a-input
                                    v-model="param.description"
                                    :disabled="isPublishedView"
                                    :placeholder="$t('chatbotv2.parameterDescription') || 'Description (help text)'"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Output Schema -->
                <div class="section">
                    <h3>{{ $t("chatbotv2.exampleOutput") || "Example Output" }}</h3>
                    <p class="section-description">
                        {{ $t("chatbotv2.exampleOutputDesc") }}
                    </p>

                    <a-form-item :label="$t('chatbotv2.outputSchemaJson')">
                        <a-textarea
                            v-model="outputSchemaText"
                            :disabled="isPublishedView"
                            :rows="10"
                            :placeholder="$t('chatbotv2.example')"
                        />
                        <div class="field-help">
                            {{ $t("chatbotv2.outputSchemaHelp") }}
                        </div>
                    </a-form-item>
                </div>

                <!-- Settings -->
                <div class="section">
                    <h3>{{ $t("chatbotv2.settings") || "Settings" }}</h3>

                    <a-form-item>
                        <a-checkbox v-model="formData.allowAutoUpdate" :disabled="isPublishedView">
                            {{ $t("chatbotv2.allowAutoUpdate") }}
                        </a-checkbox>
                        <div class="field-help">
                            {{ $t("chatbotv2.allowAutoUpdateHelp") }}
                        </div>
                    </a-form-item>

                    <a-form-item :label="$t('chatbotv2.visibility')">
                        <a-select v-model="formData.visibility" size="large" :disabled="isPublishedView">
                            <a-select-option value="public">
                                <span><a-icon type="global" /> {{ $t("chatbotv2.publicEveryone") }}</span>
                            </a-select-option>
                            <a-select-option value="unlisted">
                                <span><a-icon type="link" /> {{ $t("chatbotv2.unlistedLinkOnly") }}</span>
                            </a-select-option>
                            <a-select-option value="private">
                                <span><a-icon type="lock" /> {{ $t("chatbotv2.privateSpecificSites") }}</span>
                            </a-select-option>
                        </a-select>
                    </a-form-item>

                    <a-form-item
                        v-if="formData.visibility === 'private'"
                        :label="$t('chatbotv2.allowedSiteIds')"
                    >
                        <a-select
                            v-model="formData.allowedSiteIds"
                            mode="tags"
                            size="large"
                            :disabled="isPublishedView"
                            :placeholder="$t('chatbotv2.enterSiteIds')"
                        />
                        <div class="field-help">
                            {{ $t("chatbotv2.allowedSiteIdsHelp") }}
                        </div>
                    </a-form-item>
                </div>
            </a-form>
        </div>

        <!-- Import JSON Schema Modal -->
        <a-modal
            v-model="showJsonSchemaModal"
            :title="$t('chatbotv2.importJsonSchema')"
            :width="700"
            @ok="importJsonSchema"
            @cancel="showJsonSchemaModal = false"
        >
            <div class="json-schema-import">
                <p>{{ $t("chatbotv2.importJsonSchemaDesc") }}</p>
                <a-textarea
                    v-model="jsonSchemaInput"
                    :rows="15"
                    :placeholder="$t('chatbotv2.example')"
                />
                <div class="json-schema-help">
                    <strong>{{ $t("chatbotv2.tip") }}</strong> {{ $t("chatbotv2.jsonSchemaTip") }}
                </div>
            </div>
        </a-modal>

        <!-- System Attribute Selector Modal -->
        <a-modal
            v-model="showSystemAttrModal"
            :title="$t('chatbotv2.systemAttributeSelect')"
            :width="600"
            :footer="null"
        >
            <div class="system-attr-selector">
                <a-input-search
                    v-model="systemAttrSearch"
                    :placeholder="$t('chatbotv2.attributeSearch')"
                    style="margin-bottom: 16px"
                />
                <div class="attr-list">
                    <div
                        class="attr-item"
                        v-for="attr in filteredSystemAttributes"
                        :key="attr.value"
                        @click="selectAttribute(attr)"
                    >
                        <strong>{{ attr.name }}</strong>
                        <code v-text="`{{ \`\${attr.value}\` }}`"></code>
                    </div>
                </div>
            </div>
        </a-modal>

        <!-- Structured JSON Schema Builder Modal -->
        <a-modal
            v-model="showSchemaModal"
            :title="$t('chatbotv2.defineStructuredJsonSchema')"
            :width="700"
            @ok="saveSchema"
            @cancel="showSchemaModal = false"
        >
            <div class="schema-builder">
                <p>{{ $t("chatbotv2.structuredJsonSchemaDesc") }}</p>
                <a-textarea
                    v-model="schemaJsonInput"
                    :rows="10"
                    :placeholder="$t('chatbotv2.example')"
                />
                <div class="mt-3">
                    <a-alert type="info" show-icon>
                        <div slot="message">{{ $t("auto.blockEditorSchemaHowItWorksTitle") }}</div>
                        <div slot="description">
                            {{ $t("auto.blockEditorSchemaHowItWorksDesc") }}
                        </div>
                    </a-alert>
                </div>
            </div>
        </a-modal>

        <!-- Schema Detail Editor Modal -->
        <a-modal
            v-model="showSchemaDetailModal"
            :title="$t('chatbotv2.editStructuredJsonSchema')"
            :width="900"
            @ok="saveSchemaDetails"
            @cancel="showSchemaDetailModal = false"
        >
            <div class="schema-detail-editor">
                <div class="schema-fields-list">
                    <div v-for="(field, idx) in editingSchema" :key="idx" class="schema-field-item">
                        <div class="field-item-header">
                            <span class="field-number">#{{ idx + 1 }}</span>
                            <a-button type="danger" size="small" icon="delete" @click="removeSchemaField(idx)" />
                        </div>
                        
                        <a-row :gutter="12" class="mb-2">
                            <a-col :span="12">
                                <label>{{ $t("chatbotv2.fieldName") }} {{ $t("auto.blockEditorVariableLabelSuffix") }}</label>
                                <a-input v-model="field.name" :placeholder="$t('auto.blockEditorPlaceholderFieldName')" />
                            </a-col>
                            <a-col :span="12">
                                <label>{{ $t("chatbotv2.displayName") }}</label>
                                <a-input v-model="field.label" :placeholder="$t('auto.blockEditorPlaceholderFieldLabel')" />
                            </a-col>
                        </a-row>
                        
                        <a-row :gutter="12" class="mb-2">
                            <a-col :span="12">
                                <label>{{ $t("auto.blockEditorFieldType") }}</label>
                                <a-select v-model="field.type" style="width: 100%">
                                    <a-select-option value="text">{{ $t("auto.paramTypeText") }}</a-select-option>
                                    <a-select-option value="number">{{ $t("auto.paramTypeNumber") }}</a-select-option>
                                    <a-select-option value="boolean">{{ $t("auto.paramTypeBoolean") }}</a-select-option>
                                    <a-select-option value="array">{{ $t("auto.paramTypeArray") }}</a-select-option>
                                    <a-select-option value="userid">{{ $t("auto.paramTypeUserId") }}</a-select-option>
                                    <a-select-option value="contactid">{{ $t("auto.paramTypeContactId") }}</a-select-option>
                                    <a-select-option value="chatid">{{ $t("auto.paramTypeChatId") }}</a-select-option>
                                </a-select>
                            </a-col>
                            <a-col :span="12">
                                <label>{{ $t("chatbotv2.defaultValue") }}</label>
                                <a-input v-model="field.defaultValue" :placeholder="$t('chatbotv2.defaultValue')">
                                    <a-button slot="addonAfter" @click="selectSystemAttributeForField(idx)" size="small">
                                        <a-icon type="database" />
                                    </a-button>
                                </a-input>
                            </a-col>
                        </a-row>
                        
                        <a-row :gutter="12" class="mb-2">
                            <a-col :span="16">
                                <label>{{ $t("chatbotv2.description") }}</label>
                                <a-input v-model="field.description" :placeholder="$t('auto.blockEditorPlaceholderFieldDescription')" />
                            </a-col>
                            <a-col :span="8">
                                <label>{{ $t("chatbotv2.settings") }}</label>
                                <div>
                                    <a-checkbox v-model="field.required">{{ $t("chatbotv2.isRequired") }}</a-checkbox>
                                    <a-checkbox v-model="field.hidden" class="ml-2">{{ $t("auto.blockEditorHidden") }}</a-checkbox>
                                </div>
                            </a-col>
                        </a-row>
                    </div>
                </div>
                
                <a-button type="dashed" block icon="plus" @click="addSchemaField" class="mt-3">
                    {{ $t("chatbotv2.newFieldAdd") }}
                </a-button>
            </div>
        </a-modal>

        <!-- JavaScript Asistan Modal -->
        <a-modal
            v-model="showJsAssistant"
            :title="$t('chatbotv2.javascriptAssistant')"
            :width="800"
            :footer="null"
            @cancel="handleAssistantClose"
        >
            <div class="js-assistant-modal">
                <div class="js-assistant-description">
                    <h4>{{ $t("chatbotv2.aiWizard") }}</h4>
                    <p>
                        {{ $t("chatbotv2.aiWizardDesc") }}
                    </p>

                    <div class="js-capabilities">
                        <div class="capabilities-header">
                            <a-icon type="bulb" />
                            <h5>{{ $t("chatbotv2.youCanDo") }}:</h5>
                        </div>
                        <div class="capabilities-content">
                            <div class="capability-item">
                                <i class="material-icons">chat</i>
                                <span>{{ $t("chatbotv2.sendingMessage") }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">transfer_within_a_station</i>
                                <span>{{ $t("chatbotv2.chatTransfer") }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">close</i>
                                <span>{{ $t("chatbotv2.chatClosing") }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">http</i>
                                <span>{{ $t("chatbotv2.apiRequests") }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">person</i>
                                <span>{{ $t("chatbotv2.contactOperations") }}</span>
                            </div>
                            <div class="capability-item">
                                <i class="material-icons">settings</i>
                                <span>{{ $t("chatbotv2.attributeUpdating") }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="prompt-container">
                    <s2-textarea
                        v-model="jsPrompt"
                        :rows="4"
                        :placeholder="$t('chatbotv2.aiWizardExample')"
                        ref="promptTextarea"
                    />

                    <div class="example-prompts">
                        <div
                            class="example-prompt"
                            @click="selectExamplePrompt($t('chatbotv2.examplePrompt1'))"
                        >
                            <a-icon type="api" />
                            <span>{{ $t("chatbotv2.examplePrompt1") }}</span>
                        </div>
                        <div
                            class="example-prompt"
                            @click="selectExamplePrompt($t('chatbotv2.examplePrompt2'))"
                        >
                            <a-icon type="check-circle" />
                            <span>{{ $t("chatbotv2.examplePrompt2") }}</span>
                        </div>
                        <div
                            class="example-prompt"
                            @click="selectExamplePrompt($t('chatbotv2.examplePrompt3'))"
                        >
                            <a-icon type="code" />
                            <span>{{ $t("chatbotv2.examplePrompt3") }}</span>
                        </div>
                    </div>

                    <div class="buttons-container">
                        <a-button @click="handleAssistantClose">{{ $t("message.cancel") }}</a-button>
                        <a-button type="primary" :loading="assistantLoading" @click="generateJavaScriptCode">
                            {{ $t("chatbotv2.generateCode") }}
                        </a-button>
                    </div>
                </div>
            </div>
        </a-modal>

        <!-- Changelog Modal -->
        <Modal2
            v-if="showChangelogModal"
            :title="$t('chatbotv2.publishBlock') || 'Publish Block'"
            :description="$t('chatbotv2.enterChangelog') || 'Enter changelog (optional)'"
            :okText="$t('chatbotv2.publish') || 'Publish'"
            :cancelText="$t('message.cancel') || 'Cancel'"
            @ok="confirmPublish"
            @cancel="showChangelogModal = false"
        >
            <a-textarea
                v-model="changelogInput"
                :rows="5"
                :placeholder="$t('chatbotv2.changelogPlaceholder') || 'Describe the changes made in this version...'"
            />
        </Modal2>
    </div>
</template>

<script>
import * as monaco from "monaco-editor";
import MaterialIconPicker from "../../../Module/MaterialIconPicker.vue";
import { mapActions, mapState } from "vuex";
import { createCompletionItemProviderForChatbot } from "../../utils/createCompletionItemProviderForChatbot";
import { CUSTOM_BLOCKS_API } from "../../../../../api/chatbotCustomBlocks";
import systemAttributes from "../../utils/systemAttributes";
import CoPilot from "../../../../../utils/CoPilot";
import S2Textarea from "../../../../../components/S2Textarea.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import _ from "lodash";

export default {
    name: "BlockEditorPage",
    components: {
        MaterialIconPicker,
        S2Textarea,
        Modal2,
    },
    data() {
        return {
            form: this.$form.createForm(this),
            block: null,
            editor: null,
            saving: false,
            publishing: false,
            isFullScreen: false,
            cancels: [],
            moduleCache: [],
            showJsonSchemaModal: false,
            jsonSchemaInput: "",
            showSystemAttrModal: false,
            systemAttrSearch: "",
            selectedParamIndex: null,
            
            // Structured JSON Schema Modal
            showSchemaModal: false,
            currentSchemaParamIndex: null,
            schemaJsonInput: "",
            
            // Schema Detail Editor Modal
            showSchemaDetailModal: false,
            editingSchema: [],
            
            // Version tabs
            activeVersionTab: "draft", // draft or published
            
            showJsAssistant: false,
            jsPrompt: "",
            assistantLoading: false,
            showChangelogModal: false,
            changelogInput: "",
            outputSchemaText: "",
            categoryOptions: [
                { value: "integration", label: "Integration", icon: "api" },
                { value: "crm", label: "CRM", icon: "contacts" },
                { value: "salesforce", label: "Salesforce", icon: "cloud" },
                { value: "zoho", label: "Zoho", icon: "cloud-server" },
                { value: "hubspot", label: "HubSpot", icon: "inbox" },
                { value: "utility", label: "Utility", icon: "tool" },
                { value: "data-transform", label: "Data Transform", icon: "swap" },
                { value: "date-functions", label: "Date Functions", icon: "calendar" },
                { value: "string-functions", label: "String Functions", icon: "font-size" },
                { value: "array-functions", label: "Array Functions", icon: "unordered-list" },
                { value: "math-functions", label: "Math Functions", icon: "calculator" },
                { value: "validation", label: "Validation", icon: "check-circle" },
                { value: "formatting", label: "Formatting", icon: "edit" },
                { value: "notification", label: "Notification", icon: "notification" },
                { value: "email", label: "Email", icon: "mail" },
                { value: "sms", label: "SMS", icon: "message" },
                { value: "payment", label: "Payment", icon: "dollar" },
                { value: "database", label: "Database", icon: "database" },
                { value: "file-operations", label: "File Operations", icon: "file" },
                { value: "custom", label: "Custom", icon: "code" },
            ],
            formData: {
                name: "",
                description: "",
                icon: "extension",
                category: "custom",
                tags: [],
                code: `// Tanımladığınız parametreler otomatik olarak değişken olarak kod içinde kullanılabilir
// Örnek: "apiUrl" parametresi tanımladıysanız → doğrudan apiUrl değişkenini kullanın

async function main() {
  // Parametreleri kullanma
  // const url = apiUrl;  // apiUrl parametresi varsa direkt kullanabilirsiniz
  
  // Chatbot attribute okuma
  const userName = chatbot.readAttribute('name', 'Misafir');
  
  // HTTP isteği (parametreden gelen URL ile)
  // const response = await axios.get(apiUrl);
  
  // Mesaj gönderme
  await chatbot.sendMessage(\`Merhaba \${userName}!\`, 'text');
  
  // Attribute güncelleme
  chatbot.setAttributes({
    processed: true,
    process_date: moment().format('YYYY-MM-DD HH:mm:ss')
  });
  
  // İşlem sonucunu döndürün
  return {
    success: true,
    processedAt: moment().toISOString()
  };
}`,
                parameters: [],
                outputSchema: null,
                allowAutoUpdate: true,
                visibility: "public",
                allowedSiteIds: [],
            },
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users?.data || [],
            me: (state) => state.auth.user.user?._id,
        }),
        isEditMode() {
            return !!this.$route.params.id;
        },
        blockId() {
            return this.$route.params.id;
        },
        _() {
            return _;
        },
        filteredSystemAttributes() {
            const search = this.systemAttrSearch.toLowerCase();
            return systemAttributes.filter(
                (attr) =>
                    attr.name.toLowerCase().includes(search) || attr.value.toLowerCase().includes(search)
            );
        },
        filteredCategoryLabels() {
            return this.categoryOptions.map(cat => cat.label);
        },
        isPublishedView() {
            return this.isEditMode && this.activeVersionTab === "published";
        },
    },
    methods: {
        ...mapActions({
            createBlock: "customBlocks/createBlock",
            updateBlock: "customBlocks/updateBlock",
            publishBlock: "customBlocks/publishBlock",
        }),
        async loadBlock(forceVersion = null) {
            if (!this.blockId) return;

            try {
                const { data } = await CUSTOM_BLOCKS_API.getDetails(this.blockId, true);
                this.block = data;
                
                // Determine which version to show based on tab
                let versionData = data;
                if (forceVersion === "published" && data.publishedVersion) {
                    versionData = data.publishedVersion;
                } else if (forceVersion === "draft" && data.draftVersion) {
                    versionData = data.draftVersion;
                } else if (this.activeVersionTab === "published" && data.publishedVersion) {
                    versionData = data.publishedVersion;
                } else if (data.draftVersion) {
                    versionData = data.draftVersion;
                } else {
                    versionData = data;
                }
                
                // Ensure parameters have all required fields including schema and isList
                const normalizedParams = (versionData.parameters || []).map(param => ({
                    name: param.name || "",
                    label: param.label || "",
                    type: param.type || "text",
                    inputMode: param.inputMode || "static",
                    defaultValue: param.defaultValue !== undefined ? param.defaultValue : null,
                    required: param.required || false,
                    hidden: param.hidden || false,
                    description: param.description || "",
                    schema: param.schema || [],
                    isList: param.isList || false,
                }));
                
                this.formData = {
                    name: data.name,
                    description: data.description,
                    icon: data.icon,
                    category: data.category,
                    tags: data.tags || [],
                    code: versionData.code || data.code,
                    parameters: normalizedParams,
                    outputSchema: versionData.outputSchema || data.outputSchema,
                    allowAutoUpdate: data.allowAutoUpdate !== false,
                    visibility: data.visibility || "public",
                    allowedSiteIds: data.allowedSiteIds || [],
                };
                
                // Set output schema text for textarea
                if (versionData.outputSchema || data.outputSchema) {
                    try {
                        this.outputSchemaText = JSON.stringify(versionData.outputSchema || data.outputSchema, null, 2);
                    } catch (e) {
                        this.outputSchemaText = "";
                    }
                } else {
                    this.outputSchemaText = "";
                }
                
                if (this.editor) {
                    this.editor.setValue(this.formData.code);
                }
            } catch (error) {
                this.$message.error(error.message || "Failed to load block");
            }
        },
        onVersionTabChange(activeKey) {
            this.activeVersionTab = activeKey;
            this.loadBlock(activeKey);
            
            // Update editor readonly state
            if (this.editor) {
                this.editor.updateOptions({ readOnly: this.isPublishedView });
            }
        },
        async loadModules() {
            try {
                await this.$store.dispatch("module/syncModules");
                if (this.$store.state.module?.modules) {
                    this.moduleCache = this.$store.state.module.modules;
                }
            } catch (error) {
                console.error("Modülleri sync ederken hata oluştu:", error);
            }
        },
        initMonaco() {
            monaco.languages.typescript.javascriptDefaults.setExtraLibs([]);
            monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.ES6,
                module: monaco.languages.typescript.ModuleKind.CommonJS,
                allowNonTsExtensions: true,
                noLib: true,
            });

            this.editor = monaco.editor.create(document.getElementById("custom-block-editor-monaco"), {
                value: this.formData.code,
                language: "javascript",
                theme: "vs-dark",
                minimap: { enabled: false },
                automaticLayout: true,
                lineNumbers: "on",
                wordWrap: "on",
                tabSize: 2,
                fontSize: 14,
                readOnly: false, // Will be updated dynamically
                suggestOnTriggerCharacters: true,
                quickSuggestions: {
                    other: true,
                    comments: false,
                    strings: true,
                },
                quickSuggestionsDelay: 100,
                acceptSuggestionOnCommitCharacter: true,
                acceptSuggestionOnEnter: "on",
                suggest: {
                    showWords: false,
                    showMethods: true,
                    showFunctions: true,
                    showConstructors: true,
                    showFields: true,
                    showVariables: true,
                    showClasses: true,
                },
            });

            this.editor.onDidChangeModelContent(() => {
                if (!this.isPublishedView) {
                    this.formData.code = this.editor.getValue();
                }
            });

            // Language configuration
            monaco.languages.setLanguageConfiguration("javascript", {
                wordPattern: /(-?\d*\.\d\w*)|([^\`\~\!\@\#\$\%\^\&\*\(\)\=\+\[\{\]\}\\\|\;\:\'\"\,\.\<\>\/\?\s]+)/g,
                comments: {
                    lineComment: "//",
                    blockComment: ["/*", "*/"],
                },
                brackets: [
                    ["{", "}"],
                    ["[", "]"],
                    ["(", ")"],
                ],
                autoClosingPairs: [
                    { open: "{", close: "}" },
                    { open: "[", close: "]" },
                    { open: "(", close: ")" },
                    { open: '"', close: '"' },
                    { open: "'", close: "'" },
                    { open: "`", close: "`" },
                ],
            });

            // Autocomplete provider
            this.cancels.forEach((disposeFn) => disposeFn());
            this.cancels = [];

            const disposable = monaco.languages.registerCompletionItemProvider(
                "javascript",
                createCompletionItemProviderForChatbot([], this.moduleCache)
            );
            this.cancels.push(() => disposable.dispose());
        },
        addParameter() {
            this.formData.parameters.push({
                name: "",
                label: "",
                type: "text",
                inputMode: "static",
                defaultValue: null,
                required: false,
                hidden: false,
                description: "",
                schema: [],
                isList: false,
            });
        },
        removeParameter(index) {
            this.formData.parameters.splice(index, 1);
        },
        showImportJsonSchema() {
            this.jsonSchemaInput = "";
            this.showJsonSchemaModal = true;
        },
        importJsonSchema() {
            try {
                const json = JSON.parse(this.jsonSchemaInput);
                const newParams = [];

                Object.entries(json).forEach(([key, value]) => {
                    let type = "text";
                    let defaultValue = value;

                    // Determine type from value
                    if (typeof value === "number") {
                        type = "number";
                    } else if (typeof value === "boolean") {
                        type = "boolean";
                    } else if (Array.isArray(value)) {
                        type = "array";
                        defaultValue = value.join(", ");
                    } else if (typeof value === "object" && value !== null) {
                        type = "json";
                        defaultValue = JSON.stringify(value);
                    }

                    newParams.push({
                        name: key,
                        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
                        type: type,
                        inputMode: "static",
                        defaultValue: defaultValue,
                        required: false,
                        hidden: false,
                        description: "",
                    });
                });

                this.formData.parameters = [...this.formData.parameters, ...newParams];
                this.showJsonSchemaModal = false;
                this.$message.success(`${newParams.length} parametre eklendi`);
            } catch (error) {
                this.$message.error("Geçersiz JSON formatı: " + error.message);
            }
        },
        configureSchema(index) {
            this.currentSchemaParamIndex = index;
            const param = this.formData.parameters[index];
            
            // If schema exists, create a sample JSON from it to show
            if (param.schema && param.schema.length) {
                const sample = {};
                param.schema.forEach(f => {
                    if (f.type === 'number') sample[f.name] = 0;
                    else if (f.type === 'boolean') sample[f.name] = true;
                    else if (f.type === 'array') sample[f.name] = ["example"];
                    else sample[f.name] = "example";
                });
                
                if (param.isList) {
                    this.schemaJsonInput = JSON.stringify([sample], null, 2);
                } else {
                    this.schemaJsonInput = JSON.stringify(sample, null, 2);
                }
            } else {
                this.schemaJsonInput = "";
            }
            
            this.showSchemaModal = true;
        },
        saveSchema() {
            try {
                const json = JSON.parse(this.schemaJsonInput);
                const targetParam = this.formData.parameters[this.currentSchemaParamIndex];
                
                let sourceObj = json;
                let isList = false;
                
                if (Array.isArray(json)) {
                    isList = true;
                    if (json.length > 0) {
                        sourceObj = json[0];
                    } else {
                        throw new Error("Array boş olamaz, yapıyı anlamak için en az 1 örnek obje ekleyin.");
                    }
                }
                
                const schema = [];
                
                Object.entries(sourceObj).forEach(([key, value]) => {
                    let type = "text";
                    let defaultValue = null;
                    
                    if (typeof value === "number") {
                        type = "number";
                        defaultValue = value;
                    } else if (typeof value === "boolean") {
                        type = "boolean";
                        defaultValue = value;
                    } else if (Array.isArray(value)) {
                        type = "array";
                        defaultValue = value;
                    } else {
                        defaultValue = value;
                    }
                    
                    schema.push({
                        name: key,
                        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
                        type: type,
                        required: false,
                        hidden: false,
                        description: "",
                        defaultValue: defaultValue
                    });
                });
                
                // Update parameter
                this.$set(this.formData.parameters, this.currentSchemaParamIndex, {
                    ...targetParam,
                    schema: schema,
                    isList: isList
                });
                
                this.showSchemaModal = false;
                this.$message.success("Şema başarıyla oluşturuldu");
                
            } catch (error) {
                this.$message.error("Geçersiz JSON: " + error.message);
            }
        },
        editSchemaDetails(index) {
            this.currentSchemaParamIndex = index;
            const param = this.formData.parameters[index];
            this.editingSchema = JSON.parse(JSON.stringify(param.schema || []));
            this.showSchemaDetailModal = true;
        },
        saveSchemaDetails() {
            const targetParam = this.formData.parameters[this.currentSchemaParamIndex];
            this.$set(this.formData.parameters, this.currentSchemaParamIndex, {
                ...targetParam,
                schema: this.editingSchema
            });
            this.showSchemaDetailModal = false;
            this.$message.success("Şema detayları kaydedildi");
        },
        addSchemaField() {
            this.editingSchema.push({
                name: "",
                label: "",
                type: "text",
                required: false,
                hidden: false,
                description: "",
                defaultValue: null
            });
        },
        removeSchemaField(index) {
            this.editingSchema.splice(index, 1);
        },
        selectSystemAttributeForField(fieldIndex) {
            this.selectedParamIndex = fieldIndex;
            this.systemAttrSearch = "";
            this.showSystemAttrModal = true;
        },
        selectSystemAttribute(paramIndex) {
            this.selectedParamIndex = paramIndex;
            this.systemAttrSearch = "";
            this.showSystemAttrModal = true;
        },
        selectAttribute(attr) {
            if (this.selectedParamIndex !== null) {
                // Check if we're editing schema details
                if (this.showSchemaDetailModal) {
                    this.editingSchema[this.selectedParamIndex].defaultValue = `{{${attr.value}}}`;
                } else {
                    this.formData.parameters[this.selectedParamIndex].defaultValue = `{{${attr.value}}}`;
                }
                this.showSystemAttrModal = false;
                this.selectedParamIndex = null;
            }
        },
        showPromptWizard() {
            this.showJsAssistant = true;
            this.jsPrompt = "";
            this.$nextTick(() => {
                if (this.$refs.promptTextarea) {
                    this.$refs.promptTextarea.focus();
                }
            });
        },
        handleAssistantClose() {
            this.showJsAssistant = false;
        },
        selectExamplePrompt(prompt) {
            this.jsPrompt = prompt;
        },
        async generateJavaScriptCode() {
            if (!this.jsPrompt.trim()) {
                this.$message.warning("Lütfen bir istek yazın");
                return;
            }

            try {
                this.assistantLoading = true;

                const copilot = CoPilot.getInstance();

                // Parametre bilgilerini hazırla
                const parameterInfo = this.formData.parameters.map((p) => ({
                    name: p.name,
                    label: p.label,
                    type: p.type,
                    description: p.description,
                }));

                const response = await copilot.generateChatbotJavaScriptCode({
                    siteId: this.$store.state.site.site._id,
                    customerPrompt: this.jsPrompt,
                    chatbotAttributes: [],
                    blockId: "custom-block",
                    storyId: "custom-block",
                    currentCode: this.formData.code,
                    customBlockParameters: parameterInfo,
                    user_list: this.users.map((x) => ({
                        name: x.name,
                        email: x.email,
                        id: x._id,
                        phone: x.phone,
                        isMe: x._id === this.me,
                    })),
                    channel_list: [],
                });

                if (response && response.code) {
                    this.editor.setValue(response.code);
                    this.formData.code = response.code;
                    this.showJsAssistant = false;
                    this.$message.success("JavaScript kodu başarıyla oluşturuldu");
                } else {
                    this.$message.error("Kod oluşturulurken bir hata oluştu");
                }
            } catch (error) {
                console.error("Kod üretme hatası:", error);
                this.$message.error("Kod oluşturulurken bir hata oluştu: " + (error.message || "Bilinmeyen hata"));
            } finally {
                this.assistantLoading = false;
            }
        },
        async saveDraft() {
            if (this.isPublishedView) {
                this.$message.warning("Published version is read-only. Switch to Draft tab to edit.");
                return;
            }
            
            if (!this.formData.name || !this.formData.code) {
                this.$message.warning(this.$t("chatbotv2.fillRequired") || "Please fill required fields");
                return;
            }

            // Parse output schema from text
            if (this.outputSchemaText && this.outputSchemaText.trim()) {
                try {
                    this.formData.outputSchema = JSON.parse(this.outputSchemaText);
                } catch (e) {
                    this.$message.warning("Output schema JSON formatı geçersiz. Lütfen düzeltin veya boş bırakın.");
                    return;
                }
            } else {
                this.formData.outputSchema = null;
            }

            this.saving = true;
            try {
                if (this.isEditMode) {
                    await this.updateBlock({ id: this.blockId, updates: this.formData });
                    this.$message.success(this.$t("chatbotv2.blockUpdated") || "Block updated");
                    await this.loadBlock("draft"); // Force load draft
                } else {
                    const result = await this.createBlock(this.formData);
                    this.$message.success(this.$t("chatbotv2.blockCreated") || "Block created");
                    // Redirect to edit mode
                    await this.$router.push(`/chatbot/block-market/editor/${result._id}`);
                    // Load block data to show publish button
                    await this.loadBlock("draft");
                }
            } catch (error) {
                this.$message.error(error.message || "Failed to save block");
            } finally {
                this.saving = false;
            }
        },
        publishNow() {
            if (!this.block || !this._.get(this.block, 'draftVersion', false)) {
                this.$message.warning("Lütfen önce bloğu taslak olarak kaydedin");
                return;
            }
            this.changelogInput = "";
            this.showChangelogModal = true;
        },
        async confirmPublish() {
            try {
                this.publishing = true;
                this.showChangelogModal = false;
                await this.publishBlock({ id: this.blockId, changelog: this.changelogInput || "" });
                this.$message.success(this.$t("chatbotv2.blockPublished") || "Block published");
                await this.loadBlock("draft"); // Stay on draft tab
                this.activeVersionTab = "draft"; // Ensure we stay in draft
            } catch (error) {
                this.$message.error(error.message || "Failed to publish block");
            } finally {
                this.publishing = false;
            }
        },
        toggleFullScreen() {
            this.isFullScreen = !this.isFullScreen;
            this.$nextTick(() => {
                if (this.editor) {
                    this.editor.layout();
                }
            });
        },
        goBack() {
            this.$router.push("/chatbot/block-market");
        },
        handleResize() {
            if (this.editor) {
                this.editor.layout();
            }
        },
        handleKeyDown(e) {
            if (e.key === "Escape" && this.isFullScreen) {
                this.isFullScreen = false;
                this.$nextTick(() => {
                    if (this.editor) {
                        this.editor.layout();
                    }
                });
            }
        },
    },
    async mounted() {
        await this.loadModules();
        this.initMonaco();

        if (this.isEditMode) {
            await this.loadBlock();
        }

        window.addEventListener("resize", this.handleResize);
        window.addEventListener("keydown", this.handleKeyDown);
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.dispose();
        }
        this.cancels.forEach((cancel) => cancel());
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("keydown", this.handleKeyDown);
    },
};
</script>

<style lang="scss" scoped>
.block-editor-page {
    min-height: 100vh;
    background: #f5f5f5;

    .page-header {
        background: #fff;
        padding: 20px 32px;
        border-bottom: 1px solid #e0e0e0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: sticky;
        top: 0;
        z-index: 100;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        .header-left {
            display: flex;
            align-items: center;
            gap: 16px;

            h1 {
                margin: 0;
                font-size: 24px;
                font-weight: 600;
            }
        }

        .header-actions {
            display: flex;
            gap: 12px;
        }
    }

    .version-tabs-wrapper {
        max-width: 1400px;
        margin: 0 auto;
        padding: 32px 32px 0 32px;
    }

    .editor-content {
        max-width: 1400px;
        margin: 0 auto;
        padding: 32px;

        .section {
            background: #fff;
            padding: 32px;
            border-radius: 8px;
            margin-bottom: 24px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

            &:last-child {
                margin-bottom: 0;
            }

            h3 {
                margin: 0 0 24px 0;
                font-size: 18px;
                font-weight: 600;
                display: flex;
                align-items: center;
                justify-content: space-between;

                .param-actions {
                    display: flex;
                    gap: 8px;
                }
            }
        }

        .field-help {
            margin-top: 8px;
            font-size: 13px;
            color: #666;
        }

        .section-description {
            margin-bottom: 20px;
            color: #666;
            font-size: 14px;
        }
    }

    .code-editor-wrapper {
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;

        .code-editor-header {
            background-color: #f5f7fa;
            padding: 12px 16px;
            border-bottom: 1px solid #e0e0e0;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .tips {
                display: flex;
                flex-direction: column;
                gap: 4px;

                .tip {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    color: #647283;

                    md-icon {
                        font-size: 16px;
                    }
                }
            }

            .actions {
                display: flex;
                gap: 8px;
            }
        }

        .code-editor {
            .available-libraries,
            .available-variables,
            .attribute-help {
                padding: 8px 16px;
                background-color: #fafbfc;
                border-bottom: 1px solid #e0e0e0;
                font-size: 12px;
                color: #647283;

                span {
                    font-weight: 600;
                    margin-right: 8px;
                }

                code {
                    background-color: #fff;
                    padding: 2px 6px;
                    border-radius: 3px;
                    border: 1px solid #e0e0e0;
                    font-family: "Monaco", "Courier New", monospace;
                    font-size: 11px;
                    margin-right: 6px;
                }
            }

            .parameter-usage-info {
                padding: 12px 16px;
                background: #e6f7ff;
                border: 1px solid #91d5ff;
                border-radius: 4px;
                margin-bottom: 8px;

                .info-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #0050b3;
                    margin-bottom: 12px;
                }

                .param-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 12px;

                    .param-usage-item {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background: #fff;
                        padding: 6px 12px;
                        border-radius: 4px;
                        border: 1px solid #d9d9d9;

                        .param-var {
                            background: #f5f5f5;
                            padding: 2px 8px;
                            border-radius: 3px;
                            color: #9c27b0;
                            font-weight: 600;
                        }

                        .param-desc {
                            font-size: 12px;
                            color: #666;
                        }
                    }
                }

                .usage-examples {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;

                    .example-item {
                        font-size: 12px;
                        color: #595959;

                        strong {
                            margin-right: 8px;
                        }

                        code {
                            background: #fff;
                            padding: 4px 8px;
                            border-radius: 3px;
                            color: #d73a49;
                            font-family: "Monaco", monospace;
                        }
                    }
                }
            }

            .param-injection-notice {
                padding: 12px 16px;
                background: #fffbe6;
                border: 1px solid #ffe58f;
                border-radius: 4px;
                margin-bottom: 8px;
                display: flex;
                gap: 12px;

                .notice-content {
                    flex: 1;

                    strong {
                        color: #d46b08;
                        display: block;
                        margin-bottom: 4px;
                    }

                    p {
                        margin: 0;
                        font-size: 12px;
                        color: #595959;

                        code {
                            background: #fff;
                            padding: 2px 6px;
                            border-radius: 3px;
                            color: #9c27b0;
                            font-family: "Monaco", monospace;
                        }
                    }
                }
            }

            .monaco-editor-container {
                height: 500px;
                width: 100%;

                &.fullscreen-editor {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    z-index: 10000;
                }
            }
        }
    }

    .json-schema-import {
        .json-schema-help {
            margin-top: 12px;
            padding: 12px;
            background: #fffbe6;
            border: 1px solid #ffe58f;
            border-radius: 4px;
            font-size: 13px;
            color: #595959;
        }
    }

    .system-attr-selector {
        .attr-list {
            max-height: 400px;
            overflow-y: auto;

            .attr-item {
                padding: 12px;
                border: 1px solid #e0e0e0;
                border-radius: 4px;
                margin-bottom: 8px;
                cursor: pointer;
                transition: all 0.3s;
                display: flex;
                justify-content: space-between;
                align-items: center;

                &:hover {
                    background: #f5f5f5;
                    border-color: #9c27b0;
                }

                strong {
                    color: #333;
                }

                code {
                    background: #f5f5f5;
                    padding: 4px 8px;
                    border-radius: 3px;
                    color: #9c27b0;
                    font-size: 12px;
                }
            }
        }
    }

    .js-assistant-modal {
        .js-assistant-description {
            margin-bottom: 20px;

            h4 {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 8px;
                color: #22314a;
            }

            p {
                color: #647283;
                margin-bottom: 16px;
            }

            .js-capabilities {
                background-color: #f5f7fa;
                border-radius: 8px;
                padding: 16px;
                margin-bottom: 16px;

                .capabilities-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 12px;

                    h5 {
                        margin: 0;
                        font-size: 14px;
                        font-weight: 600;
                        color: #22314a;
                    }
                }

                .capabilities-content {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 8px;

                    .capability-item {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 13px;
                        color: #647283;

                        i {
                            font-size: 18px;
                            color: #ff6b35;
                        }
                    }
                }
            }
        }

        .prompt-container {
            .example-prompts {
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-top: 12px;
                margin-bottom: 16px;

                .example-prompt {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 12px;
                    background-color: #f5f7fa;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 13px;
                    color: #647283;

                    &:hover {
                        background-color: #e8eef3;
                        transform: translateX(4px);
                    }

                    .anticon {
                        color: #ff6b35;
                    }
                }
            }

            .buttons-container {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                margin-top: 16px;
            }
        }
    }

    .parameters-builder {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .parameter-card {
            background: #f5f5f5;
            padding: 20px;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            border: 1px solid #e0e0e0;

            .param-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;

                .param-number {
                    font-weight: 600;
                    color: #9c27b0;
                    font-size: 16px;
                }
            }

            .param-row {
                display: flex;
                gap: 12px;
                align-items: center;
            }
        }
    }
    
    .schema-detail-editor {
        .schema-fields-list {
            max-height: 500px;
            overflow-y: auto;
        }
        
        .schema-field-item {
            background: #f5f5f5;
            padding: 16px;
            border-radius: 6px;
            margin-bottom: 12px;
            border: 1px solid #e0e0e0;
            
            .field-item-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
                
                .field-number {
                    font-weight: 600;
                    color: #1890ff;
                    font-size: 14px;
                }
            }
            
            label {
                display: block;
                font-weight: 500;
                margin-bottom: 4px;
                font-size: 12px;
                color: #666;
            }
        }
    }
}
</style>
